import React, { useState } from "react";
import { Paper, Typography, Collapse, Box } from "@mui/material";
import { styled } from "@mui/system";

interface CollapsibleCardProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}

const CardContainer = styled(Paper)(() => ({
  padding: "24px",
  borderRadius: "20px",
  border: "1px solid #F5F3EF",
  backgroundColor: "#FFF",
  boxShadow: "none",
}));

const CardHeader = styled("div")(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  cursor: "pointer",
}));

const CardTitle = styled(Typography)(() => ({
  fontFamily: "Roobert, sans-serif",
  fontWeight: 500,
  fontSize: "16px",
  lineHeight: "20px",
  color: "#002035",
}));

const CardSubtitle = styled(Typography)(() => ({
  fontFamily: "Roobert, sans-serif",
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: "20px",
  color: "#5C6469",
  marginTop: "8px",
}));

const ChevronIcon = styled("div")<{ isExpanded: boolean }>(({ isExpanded }) => ({
  transition: "transform 0.3s ease",
  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const Chevron = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6 9L12 15L18 9"
      stroke="#002035"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CollapsibleCard: React.FC<CollapsibleCardProps> = ({
  title,
  subtitle,
  children,
  defaultExpanded = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <CardContainer>
      <CardHeader onClick={() => setIsExpanded(!isExpanded)}>
        <Box>
          <CardTitle>{title}</CardTitle>
          {subtitle && !isExpanded && <CardSubtitle>{subtitle}</CardSubtitle>}
        </Box>
        <ChevronIcon isExpanded={isExpanded}>
          <Chevron />
        </ChevronIcon>
      </CardHeader>
      <Collapse in={isExpanded}>
        <Box sx={{ marginTop: "16px", borderTop: "1px solid #F5F3EF", paddingTop: "16px" }}>
          {children}
        </Box>
      </Collapse>
    </CardContainer>
  );
};

export default CollapsibleCard;
